import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { observer } from 'mobx-react';

import type { InternationalAirline } from '@this/components/admin/repository_display_adjustment/types';
import { AutocompleteSelect } from '@this/shared/ui/inputs/autocomplete';
import { Button } from '@this/shared/ui/inputs/button';
import { Input } from '@this/shared/ui/inputs/input';
import { useAITTheme } from '@this/shared/ui/theme';
import ExclusionAirlineCode from '@this/src/domain/exclusion_airline_codes';

import { AdminFilterSegmentedControl } from '../share/admin_filter_segmented_control';
import { getStyles } from './repository_display_adjustment.styles';

const INPUT_TYPE_OPTIONS = [
  { value: 'autocomplete' as const, label: '選択' },
  { value: 'textfield' as const, label: '手入力' }
];

interface AirlineRowState {
  code: ExclusionAirlineCode;
  isEditing: boolean;
}

interface Props {
  airlines: InternationalAirline[];
  exclusionAirlineCodes: ExclusionAirlineCode[];
  onUpdate: (exclusionAirlineCodes: ExclusionAirlineCode[]) => Promise<void>;
}

const getAirlineDisplayName = (code: ExclusionAirlineCode, airlines: InternationalAirline[]): string => {
  if (code.inputType === 'textfield') {
    return code.airlineName.trim() || code.airlineCode || '（未設定）';
  }
  const matched = airlines.find(airline => airline.code === code.airlineCode);
  return matched?.name ?? code.airlineCode ?? '（未設定）';
};

const ExclusionAirlinesForm: React.FC<Props> = ({ airlines, exclusionAirlineCodes, onUpdate }) => {
  const { theme } = useAITTheme();
  const styles = useMemo(() => getStyles(theme), [theme]);
  const [rows, setRows] = useState<AirlineRowState[]>(() =>
    exclusionAirlineCodes.map(code => ({ code, isEditing: false }))
  );
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    setRows(exclusionAirlineCodes.map(code => ({ code, isEditing: false })));
  }, [exclusionAirlineCodes]);

  const codes = useMemo(() => rows.map(row => row.code), [rows]);
  const isChanged = codes.join('') !== exclusionAirlineCodes.join('');
  const onClickRemove = useCallback((index: number) => {
    setRows(prev => prev.filter((_, i) => i !== index));
  }, []);

  const onClickAdd = useCallback(() => {
    setRows(prev => [...prev, { code: new ExclusionAirlineCode({}), isEditing: true }]);
  }, []);

  const onClickEdit = useCallback((index: number) => {
    setRows(prev => prev.map((row, i) => (i === index ? { ...row, isEditing: true } : row)));
  }, []);

  const handleUpdate = useCallback(async () => {
    setSubmitting(true);
    try {
      await onUpdate(codes);
    } finally {
      setSubmitting(false);
    }
  }, [codes, onUpdate]);

  const handleInputTypeChange = useCallback((code: ExclusionAirlineCode, value: string) => {
    if (value === 'autocomplete' || value === 'textfield') {
      code.setInputType(value);
    }
  }, []);

  const renderEditRow = (code: ExclusionAirlineCode, index: number, isFirstInList: boolean) => (
    <div
      key={index}
      className="e2e-airline-editing-row"
      style={isFirstInList ? { ...styles.airlineRow, ...styles.airlineListRowFirst } : styles.airlineRow}
    >
      <div style={{ ...styles.airlineRowFields, order: 1 }}>
        {code.inputType === 'autocomplete' ? (
          <div style={styles.airlineAutocompleteSelect}>
            <AutocompleteSelect<InternationalAirline>
              options={airlines}
              placeholder="選択してください"
              getOptionLabel={airline => airline.name}
              value={airlines.find(airline => airline.code === code.airlineCode) ?? null}
              onChange={(_, selected) => {
                if (selected) {
                  code.changeAirlineCode(selected.code);
                }
              }}
            />
          </div>
        ) : (
          <>
            <Input
              type="text"
              value={code.airlineCode}
              onChange={e => code.changeAirlineCode(e.target.value)}
              placeholder="コード"
              style={{ ...styles.airlineFormFieldRoot, ...styles.airlineCodeInput }}
              inputProps={{ style: styles.airlineFormFieldInput }}
            />
            <Input
              type="text"
              value={code.airlineName}
              onChange={e => code.changeAirlineName(e.target.value)}
              placeholder="航空会社名"
              style={{ ...styles.airlineFormFieldRoot, ...styles.airlineNameInput }}
              inputProps={{ style: styles.airlineFormFieldInput }}
            />
          </>
        )}
      </div>
      <div style={{ ...styles.airlineRowSegment, order: 0 }}>
        <AdminFilterSegmentedControl
          name={`exclusion_airline_input_type_${index}`}
          ariaLabel="入力方法"
          options={INPUT_TYPE_OPTIONS}
          value={code.inputType}
          onChange={value => handleInputTypeChange(code, value)}
        />
      </div>
      <Button
        type="button"
        color="sub"
        size="small"
        style={{ ...styles.removeAirlineButton, ...styles.airlineRowRemoveButton, order: 2 }}
        aria-label="削除"
        onClick={() => onClickRemove(index)}
      >
        ×
      </Button>
    </div>
  );

  return (
    <>
      <div style={styles.exclusionAirlinesCard}>
        <div className="e2e-airline-list" style={styles.airlineList}>
          {rows.map((row, index) => {
            const isFirstInList = index === 0;
            if (row.isEditing) {
              return renderEditRow(row.code, index, isFirstInList);
            }
            return (
              <div
                key={index}
                className="e2e-airline-display-row"
                style={
                  isFirstInList
                    ? { ...styles.airlineDisplayRow, ...styles.airlineListRowFirst }
                    : styles.airlineDisplayRow
                }
              >
                <div className="e2e-airline-display-name" style={styles.airlineDisplayName}>
                  {getAirlineDisplayName(row.code, airlines)}
                </div>
                <div style={styles.airlineDisplayActions}>
                  <Button
                    type="button"
                    color="sub"
                    size="small"
                    style={styles.airlineEditLinkButton}
                    onClick={() => onClickEdit(index)}
                  >
                    編集
                  </Button>
                  <Button
                    type="button"
                    color="sub"
                    size="small"
                    style={styles.removeAirlineButton}
                    aria-label="削除"
                    onClick={() => onClickRemove(index)}
                  >
                    ×
                  </Button>
                </div>
              </div>
            );
          })}
          <Button
            type="button"
            color="sub"
            size="small"
            style={styles.exclusionAirlinesAddLink}
            onClick={onClickAdd}
          >
            + 航空会社を追加
          </Button>
        </div>
      </div>

      <div style={styles.exclusionAirlinesUpdateWrap}>
        <Button
          color="primary"
          style={styles.submitButton}
          onClick={handleUpdate}
          disabled={!isChanged || submitting}
        >
          更新
        </Button>
      </div>
    </>
  );
};

export default observer(ExclusionAirlinesForm);
